import * as React from "react";

import { Button as ShadcnButton } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type PlatformShortcuts = Record<string, string>;

export interface UnifiedButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  showTooltip?: boolean;
  tooltip?: React.ReactNode;
  shortcutKeys?: string;
  // Support TipTap styling states
  "data-style"?: "ghost" | "default";
  "data-active-state"?: "on" | "off";
  "data-disabled"?: boolean;
}

const MAC_SYMBOLS: PlatformShortcuts = {
  ctrl: "⌘",
  alt: "⌥",
  shift: "⇧",
} as const;

const formatShortcutKey = (key: string, isMac: boolean) => {
  if (isMac) {
    const lowerKey = key.toLowerCase();
    return MAC_SYMBOLS[lowerKey] || key.toUpperCase();
  }
  return key.charAt(0).toUpperCase() + key.slice(1);
};

const parseShortcutKeys = (
  shortcutKeys: string | undefined,
  isMac: boolean,
) => {
  if (!shortcutKeys) return [];

  return shortcutKeys
    .split("-")
    .map((key) => key.trim())
    .map((key) => formatShortcutKey(key, isMac));
};

const ShortcutDisplay: React.FC<{ shortcuts: string[] }> = ({ shortcuts }) => {
  if (shortcuts.length === 0) return null;

  return (
    <div>
      {shortcuts.map((key, index) => (
        <React.Fragment key={index}>
          {index > 0 && <kbd>+</kbd>}
          <kbd>{key}</kbd>
        </React.Fragment>
      ))}
    </div>
  );
};

export const UnifiedButton = React.forwardRef<
  HTMLButtonElement,
  UnifiedButtonProps
>(
  (
    {
      className = "",
      children,
      tooltip,
      showTooltip = true,
      shortcutKeys,
      "aria-label": ariaLabel,
      "data-style": dataStyle = "ghost",
      "data-active-state": activeState,
      "data-disabled": dataDisabled,
      ...props
    }: UnifiedButtonProps,
    ref,
  ) => {
    const isMac = React.useMemo(
      () =>
        typeof navigator !== "undefined" &&
        navigator.platform.toLowerCase().includes("mac"),
      [],
    );

    const shortcuts = React.useMemo(
      () => parseShortcutKeys(shortcutKeys, isMac),
      [shortcutKeys, isMac],
    );

    // Determine shadcn variant based on TipTap data-style
    const variant = dataStyle === "ghost" ? "ghost" : "default";

    // Detect if button has multiple elements (icon + text, icon + chevron, etc.)
    const hasMultipleElements = React.useMemo(() => {
      if (!children) return false;

      const childArray = React.Children.toArray(children);

      // More than one child element
      if (childArray.length > 1) return true;

      // Single child that contains text nodes or text + icon
      if (childArray.length === 1) {
        const child = childArray[0];
        if (React.isValidElement(child)) {
          // Check if it has children (like span with text)
          const props = child.props as {
            children?: React.ReactNode;
            className?: string;
          };
          if (props.children) {
            const grandChildren = React.Children.toArray(props.children);
            if (
              grandChildren.some(
                (gc) => typeof gc === "string" && gc.trim().length > 0,
              )
            ) {
              return true;
            }
          }
          // Check for text content in className containing "tiptap-button-text"
          if (
            typeof props.className === "string" &&
            props.className.includes("tiptap-button-text")
          ) {
            return true;
          }
        } else if (typeof child === "string" && child.trim().length > 0) {
          return true;
        }
      }

      return false;
    }, [children]);

    // Determine button size - use ghost with default size for multi-element buttons
    const size = hasMultipleElements ? "default" : "icon";

    // Apply active state styling
    const buttonClassName = cn(
      className,
      // Add active state styling
      activeState === "on" && "bg-background-10",
      dataDisabled && "opacity-50",
    );

    if (!tooltip || !showTooltip) {
      return (
        <ShadcnButton
          variant={variant}
          size={size}
          className={buttonClassName}
          ref={ref}
          aria-label={ariaLabel}
          data-style={dataStyle}
          data-active-state={activeState}
          data-disabled={dataDisabled}
          {...props}
        >
          {children}
        </ShadcnButton>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <ShadcnButton
            variant={variant}
            size={size}
            className={buttonClassName}
            ref={ref}
            aria-label={ariaLabel}
            data-style={dataStyle}
            data-active-state={activeState}
            data-disabled={dataDisabled}
            {...props}
          >
            {children}
          </ShadcnButton>
        </TooltipTrigger>
        <TooltipContent>
          <div>{tooltip}</div>
          <ShortcutDisplay shortcuts={shortcuts} />
        </TooltipContent>
      </Tooltip>
    );
  },
);

UnifiedButton.displayName = "UnifiedButton";
